/**
 * Khung ứng dụng: dải cảnh báo NGUY + thanh trên + thanh bên + vùng nội dung +
 * **thanh trạng thái dưới**.
 *
 * Đặc tả: `_BUS/31_QUY_HOACH_APP.md` PHẦN 5B. Bốn thay đổi, xếp theo "mắt nhận ra ngay":
 *
 *   🥇 1 · thanh trạng thái dưới, cố định đáy, KHÔNG BAO GIỜ cuộn mất
 *   🥈 2 · gom 7 mục phẳng thành 5 nhóm có tiêu đề
 *   🥉 3 · dải cảnh báo mức NGUY dính dưới thanh trên — không có thì BIẾN MẤT HẲN
 *      4 · `w-72` → `w-60`, hàng điều hướng thấp lại
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * 🔴 VÌ SAO THANH DƯỚI LÀ THỨ THIẾU QUAN TRỌNG NHẤT (lee)
 *
 * *"Đây là chi tiết mắt dùng để phân biệt 'phần mềm' với 'trang web' nhanh hơn mọi thứ
 * khác. Phần mềm điều khiển LUÔN nói cho người trực biết nó còn sống — không bắt người ta
 * đi tìm."*
 *
 * Nên nó `fixed`, không `sticky`: `sticky` còn phụ thuộc vào chuyện cha nó có cuộn hay
 * không, `fixed` thì không phụ thuộc gì cả.
 *
 * ⚠ Trên điện thoại các mục bấm được vẫn phải cao ≥40px (kỹ thuật viên hiện trường dùng
 *   một tay). Nên hàng điều hướng là **40px ở màn hình nhỏ, 32px từ `lg` trở lên** —
 *   mật độ là việc của màn hình lớn, không phải cái cớ để bóp nút trên điện thoại.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Activity, AlertOctagon, BellRing, CalendarClock, ChevronDown, ChevronLeft, ChevronRight, ClipboardList,
  FlaskConical, Gauge, KeyRound, LayoutDashboard, LogOut, MapPin, Menu, Moon, Palette, Search,
  ShieldCheck, SlidersHorizontal, Sun, Type, Users as UsersIcon, X, Zap,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { OKhoaNho } from './Khoa';
import { useGiaoDien, NHAN_BO_CHU, NHAN_CHE_DO, type BoChu, type CheDo } from '../lib/giaodien';
import { tuGanNhat } from '../lib/tugannhat';
import { useAlarms, useMoPhong, useOverview, useRealtimeStatus, useRealtimeSync, useTree } from '../lib/queries';
import { ROLE_LABEL, type Alarm, type Role, type TrangThaiMoPhong, type TreeCabinet, type TreeRoute } from '../lib/types';
import { Badge, Button, Modal, Field, StatusDot, useToast, LOP_CHUA_RO } from './ui';
import { errorMessage } from '../lib/api';
import { fmtTime } from '../lib/format';
import { cauDoTuoi, locMucNguy, phanMuc } from '../lib/canhbao';
import { useGioMayChu } from '../lib/giomaychu';
import { hangKhop, soSanhNhay } from '../lib/xephang';
import PopupCanhBao from './PopupCanhBao';
/*
 * ➕ `GD-07A` — SỐ PHIÊN BẢN ĐỌC TỪ `package.json`, không gõ tay. Xem `TranhThanhPho()` ở
 *   cuối tệp: một con số gõ tay ở đây sẽ nói dối ngay lần phát hành sau, và nó nói dối đúng
 *   vào lúc người trực gọi điện báo lỗi và được hỏi *"anh đang chạy bản nào"*.
 * ⚠ Nhập trường `version` THÔI, không nhập cả tệp: Vite nhúng thẳng lúc dựng gói, và cách này
 *   không kéo theo danh sách phụ thuộc của dự án vào gói giao diện.
 */
import { version as PHIEN_BAN } from '../../package.json';

interface NavItem {
  to: string;
  /**
   * TÊN ĐẦY ĐỦ — dùng cho `title`, ô gõ-để-nhảy, và mọi chỗ có đủ bề ngang.
   * ⛔ Đây vẫn là tên CHÍNH THỨC của mục (YC-33 chốt "Quản lý Tuyến chiếu sáng").
   */
  label: string;
  /**
   * NHÃN NGẮN cho thanh rail 88px.
   *
   * 🔴 Vì sao phải có trường thứ hai thay vì cắt chuỗi: rail rộng 88px chở được ~10 ký tự.
   *   `label` dài nhất là 24 ký tự ("Quản lý Tuyến chiếu sáng"). Cắt tự động ra
   *   `Quản lý Tuyế…` — một cái tên **không ai đặt**, và người đọc không biết nó là gì.
   *
   * ⛔ Và đây KHÔNG phải tên thứ hai cho cùng một thứ (đúng cái L67 vừa dẹp): nhãn rail là
   *   một **chữ viết tắt có chủ**, còn tên đầy đủ vẫn ở `title` khi rê chuột, ở ô gõ-để-nhảy,
   *   và ở tiêu đề H1 của chính trang ấy. Một tên chính thức, một chữ tắt — không phải hai tên.
   */
  nhanRail: string;
  icon: typeof LayoutDashboard;
  /*
   * ⛔ `minRole` GIỮ LẠI NHƯNG THÔI DÙNG cho mục nào có `khoa` — `P-6` `B4` mục 3 chuyển ẩn/hiện
   *   sang TICK. Không xoá hẳn trường: ngày có một khu vực chỉ dành cho `SUPER_ADMIN` thì hạng
   *   vẫn là câu hỏi đúng, và máy chủ cũng còn dùng hạng cho đúng ba tuyến ấy.
   */
  minRole?: Role;
  /** Tick phải có để THẤY mục này. Một mục có `khoa` thì `minRole` không còn được đọc. */
  khoa?: string;
  /** Mục cần một CỜ TUYẾN bất kỳ (vd điều khiển) — hạng và tick đều không trả lời được câu này. */
  canCoTuyen?: 'canControl' | 'canEditSchedule';
  end?: boolean;
}

interface NavGroup {
  /** Tiêu đề nhóm: chữ nhỏ, hoa, nhạt, KHÔNG bấm được. */
  tieuDe: string;
  muc: NavItem[];
}

/**
 * NĂM NHÓM — theo **câu hỏi người trực đang hỏi**, không theo bảng CSDL trang đó đọc
 * *(luật phân mô-đun, PHẦN 1)*.
 *
 * ⚠️ Đây là gom nhóm ở **thanh điều hướng**. Không tệp nào bị đổi tên, không đường dẫn nào
 *    bị đổi, không màn hình nào được thêm — đúng cái lee cho phép chạy trước 0–G.
 *
 * ⚠️ Đặc tả xếp *"Danh sách chờ"* vào nhóm QUẢN TRỊ. Nó **không có đường dẫn riêng** —
 *    nó là một khối bên trong `/con-duong`. Thêm một mục điều hướng cho nó là **thêm màn
 *    hình**, đúng thứ đang bị chặn tới sau 0–G. Nên vẫn là 7 mục, gom thành 5 nhóm.
 */
/** Khoá nhớ trạng thái cột cây. Cùng tiền tố `sdkd.` với các khoá phiên. */
const KHOA_CAY = 'sdkd.cotCay';

const NHOM_NAV: NavGroup[] = [
  {
    tieuDe: 'Giám sát',
    muc: [{ to: '/', label: 'Tổng quan', nhanRail: 'Tổng quan', icon: LayoutDashboard, end: true }],
  },
  {
    tieuDe: 'Điều khiển',
    muc: [{ to: '/dieu-khien', label: 'Điều khiển theo tuyến', nhanRail: 'Điều khiển', icon: SlidersHorizontal, canCoTuyen: 'canControl' }],
  },
  {
    tieuDe: 'Cấu hình',
    muc: [
      { to: '/lich', label: 'Lịch đóng cắt', nhanRail: 'Lịch', icon: CalendarClock },
      { to: '/chien-luoc', label: 'Chiến lược', nhanRail: 'Chiến lược', icon: Gauge },
    ],
  },
  {
    tieuDe: 'Quản trị',
    muc: [
      { to: '/con-duong', label: 'Quản lý Tuyến chiếu sáng', nhanRail: 'Tuyến', icon: MapPin, khoa: 'quan_ly_tu' },
      /*
       * ⚠ ĐỔI TÊN: "Quản trị người dùng" → **"Quản trị"** (lee đặt hàng, `B4` mục 1). Màn hình
       *   ấy nay không chỉ là danh sách người dùng: nó cấp TUYẾN, cấp TICK và đặt THỜI HẠN —
       *   một cái tên chỉ nói "người dùng" sẽ khiến người ta không tìm thấy ba việc kia.
       */
      { to: '/quan-tri', label: 'Quản trị', nhanRail: 'Quản trị', icon: UsersIcon, khoa: 'tao_tai_khoan' },
    ],
  },
  {
    tieuDe: 'Nhật ký',
    /*
     * ═══════════════════════════════════════════════════════════════════════════════════
     * ➕ `v4` `S6` — MỤC NÀY BỎ `khoa`, tức là **AI CŨNG THẤY**. (lee dò phân bậc.)
     *
     * 🔴 Trước bản này nó gác bằng `tao_tai_khoan`, và hệ quả là một tài khoản **vận hành
     *   không tra nổi việc của chính mình**. Ca thật: người trực bị hỏi *"lúc 21:14 ai tắt
     *   tuyến 3"*, hàng nhật ký ấy mang đúng tên họ, mà họ phải đi xin một người có tick để
     *   đọc một dòng nói về mình.
     *
     * ⛔ VÀ ĐÂY LÀ MỘT KIỂU GÁC KHÁC HẲN, không phải một lần nới lỏng: **cửa vẫn có, nhưng nó
     *   gác PHẠM VI chứ không gác LỐI VÀO**. Máy chủ ép `userId = tôi` cho người không có tick
     *   (`requireQuyenHoacCuaToi` + `phamvinhatky.ts`). Ẩn cả mục menu là cách gác thô nhất:
     *   nó đúng ở vế "không cho xem của người khác" và sai ở vế "không cho xem của mình".
     * ⚠ Nên mục này KHÔNG có `khoa`, và điều đó phải được canh: một `khoa` thêm lại vào đây
     *   sẽ đóng đúng cái vừa mở, mà màn hình thì trông y hệt (chỉ thiếu một dòng menu).
     * ═══════════════════════════════════════════════════════════════════════════════════
     */
    muc: [{ to: '/nhat-ky', label: 'Nhật ký thao tác', nhanRail: 'Nhật ký', icon: ClipboardList }],
  },
];

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * MỘT CHỖ QUYẾT ĐỊNH "THẤY MỤC NÀY KHÔNG" — `P-6` `B4` mục 3.
 *
 * ⛔ BA chỗ đọc `NHOM_NAV` (thanh rail · ngăn kéo · ô gõ-để-nhảy) và trước bản này cả ba đều
 *   tự viết `!m.minRole || hasRole(m.minRole)`. Ba bản của một luật là ba bản sẽ trôi khỏi
 *   nhau — và chỗ trôi sẽ là chỗ **ô gõ-để-nhảy vẫn nhảy tới một trang đã bị giấu khỏi menu**.
 *   Nay một hàm, ba chỗ gọi.
 *
 * ⚠ BA CÂU HỎI KHÁC NHAU, theo đúng thứ tự: tick → cờ tuyến (bất kỳ tuyến nào) → hạng.
 *   Mục không khai gì thì ai cũng thấy (Tổng quan, Lịch, Chiến lược — chúng tự gác bên trong).
 */
/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ➕ `GD-09` mục 4 — MỘT MỤC MENU CÓ **BA** TRẠNG THÁI, không phải hai.
 *
 * 🔴 HAI LỖI lee chụp 14/09, và chúng là MỘT:
 *     · `Khach_hang_1` có tài khoản phụ (⇒ phải có tick `tao_tai_khoan`) mà **không thấy** mục
 *       Quản trị;
 *     · `khach_hang_11` ở Tổng quan không thấy "Điều khiển theo tuyến", sang trang tủ thì thấy.
 *   Gốc: hàm cũ trả `boolean`, và chỗ gọi **lọc mục đi** khi nó trả `false`. Nên một câu chưa
 *   đo được (`/api/auth/me` chưa trả lời — xem `daBietQuyen`) biến thành một mục **biến mất**.
 *   Menu khác nhau giữa hai trang chỉ vì hai trang được vẽ ở hai thời điểm.
 *
 * ⛔ VÀ CẢ KHI ĐÃ ĐO XONG, ẨN VẪN LÀ SAI Ở ĐÂY. Luật của dự án là **khoá-không-ẩn**: một mục
 *   khoá là một câu trả lời (*"có thứ này, bạn chưa được cấp, đi hỏi ai"*); một khoảng trống
 *   thì không nói gì, và người dùng không biết để mà đi xin. Luật ấy đã áp cho ô tick, cho nút
 *   bấm, cho thẻ tuyến — menu là chỗ cuối cùng còn ẩn.
 * ⚠ NGOẠI LỆ CÓ CHỦ Ý, giữ nguyên: mục "Nhật ký thao tác" KHÔNG khai `khoa` — ai cũng thấy,
 *   nội dung tự co theo quyền. Đó là một quyết định cũ, không phải một chỗ sót.
 *
 * ⇒ Ba trạng thái, và chỗ gọi vẽ cả ba:
 *     `'mo'`        bấm được
 *     `'khoa'`      hiện + ổ khoá hổ phách, bấm vào thì trang nói rõ thiếu tick nào
 *     `'chua-biet'` hiện + xám nhạt, KHÔNG hổ phách (chưa đo được thì chưa khai)
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
type TrangThaiMuc = 'mo' | 'khoa' | 'chua-biet';

/**
 * Dấu bên phải một mục menu — **MỘT chỗ vẽ, hai thanh dùng** (rail + ngăn kéo).
 *
 * ⛔ Hai chỗ tự vẽ là hai chỗ sẽ lệch, và lệch ở đây nghĩa là cùng một tài khoản thấy hai câu
 *   khác nhau tuỳ bề rộng cửa sổ — đúng cái bệnh `GD-09` mục 4 vừa chữa, tái phát ở một chiều
 *   khác.
 * ⚠ `'khoa'` mượn `HuyHieuKhoa` của `Khoa.tsx` (ổ khoá hổ phách, một nguồn cho cả app), nhưng
 *   KHÔNG kèm chữ: ở menu không có chỗ cho một câu, và câu đầy đủ nằm ở trang đích.
 */
function DauTrangThaiMuc({ tt }: { tt: TrangThaiMuc }) {
  if (tt === 'mo') return null;
  if (tt === 'chua-biet') {
    /* Xám, đập nhẹ — "đang đo", không phải "không có quyền" (`[95]`). */
    return (
      <span
        className="ml-auto h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-chua-ro"
        title="Đang kiểm quyền…"
        aria-label="Đang kiểm quyền"
      />
    );
  }
  /*
   * ⛔ Ổ KHOÁ LẤY TỪ `Khoa.tsx` — biểu tượng ấy chỉ được vẽ ở MỘT chỗ trong cả app. Bản đầu
   *   tôi nhập `Lock` của `lucide-react` vào đây và bộ kiểm đỏ đúng: hai chỗ tự vẽ là hai cái
   *   ổ khoá sẽ khác nhau, mà người dùng nhận ra "khoá" bằng HÌNH chứ không bằng tên lớp.
   */
  return <OKhoaNho className="ml-auto h-3.5 w-3.5 shrink-0" />;
}

function useTrangThaiMuc(): (m: NavItem) => TrangThaiMuc {
  const { hasRole, coQuyen, user, daBietQuyen } = useAuth();
  return (m: NavItem) => {
    /*
     * ⛔ HỎI "ĐÃ BIẾT CHƯA" TRƯỚC MỌI CÂU KHÁC — và chỉ cho những mục THẬT SỰ hỏi quyền.
     *   Một mục không khai gì (Tổng quan, Lịch, Chiến lược) thì không có gì để chờ biết.
     */
    if ((m.khoa || m.canCoTuyen) && !daBietQuyen) return 'chua-biet';
    if (m.khoa) return coQuyen(m.khoa) ? 'mo' : 'khoa';
    if (m.canCoTuyen) {
      /* “Có ÍT NHẤT một tuyến mang cờ ấy” — `SUPER_ADMIN` qua bằng vế bỏ qua. */
      if (user?.role === 'SUPER_ADMIN') return 'mo';
      return (user?.routeAccess ?? []).some((r) => r[m.canCoTuyen!]) ? 'mo' : 'khoa';
    }
    if (m.minRole) return hasRole(m.minRole) ? 'mo' : 'khoa';
    return 'mo';
  };
}


/*
 * Cao 28px (`h-7`). Thanh bên và vùng nội dung đều phải chừa đúng chừng này.
 *
 * 🔴 CHIỀU CAO thanh trạng thái, và CHỖ ĐỨNG ngay trên nó — MỘT nguồn, BA người dùng.
 *   Thanh Gửi của trang Cài đặt phải dính đáy NGAY TRÊN thanh này. Viết `bottom-7` ở đó và
 *   `h-7` ở đây là hai con số phải bằng nhau mà không gì bắt chúng bằng nhau — đổi một cái
 *   là cái kia âm thầm sai, và chỉ ẢNH mới thấy (đã gặp: `bottom-0` bị thanh này đè lên nút).
 */
export const CAO_THANH_DUOI = 'h-[calc(1.75rem+env(safe-area-inset-bottom))] pb-[env(safe-area-inset-bottom)]';
export const DAY_THANH_DUOI = 'bottom-[calc(1.75rem+env(safe-area-inset-bottom))]';

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ĐỘ LỆCH THEO **SỐ DẢI ĐANG HIỆN** — 0, 1 hoặc 2 (dải Nguy · dải Mô phỏng).
 *
 * ⚠ Vì sao phải là BẢNG chứ không phải phép nhân: Tailwind quét mã nguồn tìm chuỗi lớp có
 *   sẵn. `` `top-[${56 + 32 * soDai}px]` `` biên dịch trót lọt, `tsc` xanh, và ra trình duyệt
 *   thì **không có lớp nào cả** — thanh bên tụt lên nằm dưới dải. Đúng loại lỗi chỉ ảnh chụp
 *   bắt được, nên chặn nó bằng cấu tạo: ba chuỗi viết sẵn, không có đường nào sinh ra chuỗi.
 *
 * ⛔ Mỗi dải cao ĐÚNG `h-8` = 32px, và cả hai dải đều `fixed`. Sửa chiều cao một dải mà không
 *   sửa bảng này là làm `aside`/`main` lệch âm thầm.
 *
 *   số dải   dải đứng ở            `top` của aside        `pt` của main
 *   ──────   ──────────────────    ──────────────────     ─────────────────
 *     0      —                     56px  (`top-14`)       16px (`pt-4`)
 *     1      56                    88px  (`5.5rem`)       48px (`pt-12`)
 *     2      56 · 88               120px (`7.5rem`)       80px (`pt-20`)
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export const TOP_THEO_SO_DAI = [
  'top-[calc(3.5rem+env(safe-area-inset-top))]',
  'top-[calc(5.5rem+env(safe-area-inset-top))]',
  'top-[calc(7.5rem+env(safe-area-inset-top))]',
] as const;
export const CHUA_TREN_THEO_SO_DAI = ['pt-4', 'pt-12', 'pt-20'] as const;

/** Chỗ đứng của chính các dải: dải Nguy luôn sát thanh trên, dải Mô phỏng xếp dưới nó. */
export const TOP_DAI = [
  'top-[calc(3.5rem+env(safe-area-inset-top))]',
  'top-[calc(5.5rem+env(safe-area-inset-top))]',
] as const;

export default function Layout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  /*
   * Cột cây mở hay đóng — nhớ giữa các lần mở trang.
   * ⚠ Bọc `try/catch`: cửa sổ riêng tư / trình duyệt chặn lưu trữ thì `localStorage` NÉM,
   *   và một thanh điều hướng không được phép làm sập cả trang vì một tuỳ chọn hiển thị.
   */
  /*
   * ➕ `GD-02` — MẶC ĐỊNH **ĐÓNG**, đảo lại so với bản cũ.
   *
   * 🔴 Mẫu lee duyệt chỉ có MỘT thanh bên 200 px; cột cây tuyến không có trong đó. Nhưng cây
   *   ấy là một đường đi thật *(từ bất kỳ trang nào tới thẳng một cái tủ)*, nên **xoá nó là
   *   lấy mất một chức năng để đổi lấy một cái ảnh**. ⇒ Giữ nguyên cột, chỉ đổi mặc định:
   *   màn hình mở ra đúng như mẫu, và ai cần cây thì bấm một nút — nút vẫn ở chỗ cũ.
   * ⚠ `=== '1'` chứ không `!== '0'`: khoá VẮNG MẶT nay nghĩa là đóng. Người đã từng mở cây
   *   thì `'1'` còn trong `localStorage` và họ vẫn thấy nó mở — không ai bị giật mất thứ họ
   *   đã chọn.
   */
  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * ⚠ `GD-04` 11/09/2026 — MẶC ĐỊNH ĐẢO LẠI LẦN NỮA: **MỞ ở màn ≥ 1280 px, ĐÓNG ở màn hẹp**.
   *   Và tôi ghi cả ba lần đổi lại đây, vì một mặc định bị lật ba lần trong hai ngày là thứ
   *   lần sau người ta sẽ lật lần thứ tư mà không biết vì sao nó từng là thế:
   *       bản đầu   MỞ      — cột cây là đường đi nhanh tới một cái tủ
   *       `GD-02`   ĐÓNG    — mẫu Tổng quan chỉ có MỘT thanh bên 200 px
   *       `GD-04`   theo BỀ RỘNG — mẫu `24` vẽ cây ấy **nằm trong ảnh**, cạnh menu
   *   Mẫu `GD-02` không vẽ cây vì nó là mẫu của một màn hẹp hơn; mẫu `GD-04` vẽ nó. Hai mẫu
   *   không mâu thuẫn — chúng nói về hai bề rộng khác nhau, và đó chính là câu trả lời.
   *
   * 🔴 BA CA, VÀ CA GIỮA LÀ CA DỄ NUỐT: khoá `'1'`/`'0'` là **lựa chọn người dùng đã nói ra**
   *   và nó thắng mọi mặc định; khoá **VẮNG MẶT** nghĩa là *"người này chưa bao giờ nói gì"*,
   *   và chỉ lúc ấy mới hỏi tới bề rộng màn hình. Đọc `null` thành `'0'` là biến "chưa chọn"
   *   thành "đã chọn đóng" — Luật số 0, ở một chỗ trông như chuyện vặt.
   * ⚠ `matchMedia` bọc `try/catch` cùng với `localStorage`: cả hai đều ném được (cửa sổ riêng
   *   tư, môi trường dựng sẵn không có `window`), và một tuỳ chọn hiển thị không được phép
   *   làm sập cả trang.
   * ⚠ Đo MỘT LẦN lúc dựng, không theo dõi liên tục: người dùng kéo hẹp cửa sổ rồi cây tự
   *   đóng lại giữa lúc họ đang đọc là một cú giật, không phải một sự giúp đỡ. Lớp `lg:` của
   *   Tailwind đã lo phần ẩn cột ở màn hẹp.
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  const [moCay, setMoCayRaw] = useState<boolean>(() => {
    try {
      const daChon = localStorage.getItem(KHOA_CAY);
      if (daChon === '1') return true;
      if (daChon === '0') return false;
      return window.matchMedia('(min-width: 1280px)').matches;
    } catch { return false; }
  });
  const datMoCay = (v: boolean) => {
    setMoCayRaw(v);
    try { localStorage.setItem(KHOA_CAY, v ? '1' : '0'); } catch { /* không sao */ }
  };
  const location = useLocation();
  const { data: canhBao } = useAlarms();
  const nguy = locMucNguy(canhBao);

  /*
   * 🔴 CHỖ NÀY CHỈ THẤY ĐƯỢC BẰNG ẢNH CHỤP.
   *
   * Bản đầu để dải Nguy là `sticky` trong dòng chảy, còn `aside` thì `fixed top-14`.
   * Kết quả: dải **đè lên 32px đầu của thanh bên** và **nuốt mất tiêu đề nhóm GIÁM SÁT**.
   * Cả 20 phép của [36] đều xanh — vì mã đúng **hình dạng**, chỉ sai **chỗ đứng**.
   *
   * ⇒ Dải thành `fixed` cao đúng `h-8`, và cả `aside` lẫn `main` cùng đọc MỘT biến `nguy`
   *   để tụt xuống đúng chừng ấy. Một nguồn, ba chỗ dùng — không ai lệch được khỏi ai.
   *
   * 🎯 lee đúng: *"với việc về diện mạo, một ảnh chụp là phép đo, còn 421/0 thì không."*
   */
  const coNguy = nguy.length > 0;

  /*
   * 🔴 DẢI THỨ HAI: "ĐANG MÔ PHỎNG" (lee giao 07/09, dev_device nhắc *"đừng để nó trôi"*).
   *
   * ⛔ Và đây là chỗ dễ hỏng nhất khi thêm dải: đoạn ba dòng trên vốn là `coNguy ? A : B` —
   *   một câu hỏi CÓ/KHÔNG. Thêm một dải nữa mà giữ nguyên hình dạng ấy thì hoặc dải mới tự
   *   đặt độ lệch riêng (rồi lệch khỏi `aside`/`main`), hoặc hai dải chồng lên nhau. Chính là
   *   cái bệnh ảnh chụp đã bắt được lần trước, chỉ khác con số.
   *
   * ⇒ Câu hỏi đổi từ "có nguy không" thành **"đang có MẤY dải"**. Vẫn MỘT nguồn (`soDai`),
   *   ba chỗ dùng (`aside` rail · `aside` cây · `main`) — không ai lệch được khỏi ai.
   */
  const { data: moPhong } = useMoPhong();
  const coMoPhong = moPhong?.bat === true;
  const soDai = (coNguy ? 1 : 0) + (coMoPhong ? 1 : 0);
  const topBen = TOP_THEO_SO_DAI[soDai];
  const chuaTren = CHUA_TREN_THEO_SO_DAI[soDai];

  const [oNhayMo, setONhayMo] = useState(false);

  /*
   * ⌨️ Ctrl+K / ⌘K. Nghe ở `window` chứ không ở một phần tử — người dùng có thể đang đứng
   *   bất kỳ đâu trong app khi họ muốn nhảy.
   * ⚠ `preventDefault()` là bắt buộc: Chrome gán Ctrl+K cho thanh địa chỉ của chính nó.
   */
  useEffect(() => {
    const nghe = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setONhayMo((v) => !v);
      }
    };
    window.addEventListener('keydown', nghe);
    return () => window.removeEventListener('keydown', nghe);
  }, []);

  // Nghe WebSocket một lần cho cả app
  useRealtimeSync();

  // Đổi trang thì đóng ngăn kéo trên điện thoại
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-nen-2">
      <TopBar onMenu={() => setDrawerOpen(true)} moODeNhay={() => setONhayMo(true)} />
      <DaiCanhBaoNguy nguy={nguy} />
      <DaiMoPhong tt={moPhong} coNguy={coNguy} />

      <div className="flex">
        {/*
          ═══════════════════════════════════════════════════════════════════════════════
          HAI CỘT (lee chốt 05/09): rail icon 88px, rồi cột cây tuyến 240px mở/đóng được.

          ⚠ Vì sao cây phải là CỘT RIÊNG chứ không nhét vào rail: rail 88px không chở nổi ô
            tìm lẫn tên tuyến đầy đủ (`tenDayDu` = tên + địa chỉ, chốt YC-21 cấm hiện tên
            trơ). Ép nó vào rail là ép đúng thứ YC-21 đã cấm.

          ⚠ Trạng thái mở/đóng nhớ qua `localStorage`: người trực ca đóng cây một lần rồi
            F5 mà nó mở lại thì họ phải đóng lại mỗi lần — một thao tác lặp không ai xin.
        */}
        {/*
          ➕ `GD-02` — MỘT thanh bên 200 px theo mẫu lee duyệt, thay cho rail 88 px.
          ⚠ `w-50` không có trong Tailwind mặc định ⇒ `w-[200px]`, đúng con số mẫu.
        */}
        <aside className={`fixed ${DAY_THANH_DUOI} left-0 ${topBen} z-30 hidden w-[200px] border-r border-vien bg-nen-1 lg:block`}>
          <ThanhBenChinh />
        </aside>
        {moCay && (
          <aside className={`fixed ${DAY_THANH_DUOI} left-[200px] ${topBen} hidden w-60 border-r border-vien bg-nen-1 lg:block`}>
            <SidebarContent hienNav={false} />
          </aside>
        )}
        {/*
          Nút mở/đóng cột cây — dính vào MÉP PHẢI của cột (hoặc của rail khi đã đóng), để nó
          luôn nằm ngay chỗ người ta vừa nhìn, không phải đi tìm ở một góc khác.
        */}
        <button
          onClick={() => datMoCay(!moCay)}
          title={moCay ? 'Thu gọn cây tuyến chiếu sáng' : 'Mở cây tuyến chiếu sáng'}
          aria-label={moCay ? 'Thu gọn cây tuyến chiếu sáng' : 'Mở cây tuyến chiếu sáng'}
          className={`fixed top-1/2 z-40 hidden -translate-y-1/2 rounded-r-lg border border-l-0 border-vien bg-nen-1 py-3 text-chu-3 hover:bg-nen-2 hover:text-chu-1 lg:block ${moCay ? 'left-[440px]' : 'left-[200px]'}`}
        >
          {moCay ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>

        {/* Ngăn kéo trên điện thoại */}
        {drawerOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="absolute inset-0 bg-nen-0/80" onClick={() => setDrawerOpen(false)} />
            {/*
              ⚠ NGĂN KÉO DI ĐỘNG GIỮ NHƯ CŨ (phiếu chốt) — nhưng nay nó chở **thanh bên mới**
                rồi tới cây tuyến, thay vì chở một bản điều hướng thứ hai. Một danh sách mục,
                một chỗ dựng: hai bản chép là hai bản sẽ trôi khỏi nhau.
            */}
            <aside className="absolute inset-y-0 left-0 flex w-[86%] max-w-xs flex-col overflow-y-auto bg-nen-1 pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] shadow-xl">
              <div className="flex items-center justify-between border-b border-vien px-4 py-3">
                <span className="flex items-center gap-2 font-semibold text-nhan">
                  <Zap className="h-5 w-5" /> SDKD Chiếu sáng
                </span>
                <button onClick={() => setDrawerOpen(false)} className="rounded-lg p-1 text-chu-3 hover:bg-nen-2" aria-label="Đóng menu">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <ThanhBenChinh giua={<SidebarContent hienNav={false} />} />
            </aside>
          </div>
        )}

        {/* `pb-10` để dòng cuối không nằm dưới thanh trạng thái */}
        <main className={`min-w-0 flex-1 px-3 pb-10 ${chuaTren} sm:px-5 ${moCay ? 'lg:ml-[440px]' : 'lg:ml-[200px]'}`}>
          <Outlet />
        </main>
      </div>

      <ThanhTrangThai moODeNhay={() => setONhayMo(true)} />
      <ODeNhay mo={oNhayMo} dong={() => setONhayMo(false)} />
      {/*
        🔴 POPUP MỨC 1 (YC-29 PHẦN 2) — treo ở KHUNG, không ở một trang.
        Người trực đang mở trang Lịch mà một tủ mất bảo vệ thì họ vẫn phải thấy. Đặt trong
        một trang là làm cảnh báo phụ thuộc vào chỗ người ta đang đứng.
      */}
      <PopupCanhBao />
    </div>
  );
}

/* --------------------------------------------- 🥉 dải cảnh báo mức NGUY */

/**
 * Dính ngay dưới thanh trên, chỉ **mức 1**. Mức 2 và 3 nằm trong trang.
 *
 * ⛔ KHÔNG có cảnh báo mức 1 thì dải **biến mất hẳn** — không vẽ một dải xanh "mọi thứ ổn".
 *
 * > lee: *"Một dải luôn hiện thì mắt thôi nhìn nó."*
 *
 * Cùng đúng lý do `dienGiaiMaLoi()` bên máy chủ từ chối dựng cảnh báo cho mã lạ: một bảng
 * đầy dòng vô nghĩa thì **cảnh báo thật cũng chìm theo**.
 *
 * ⚠ `top-14` = ngay dưới thanh trên; `sticky` chứ không `fixed`, để nó đẩy nội dung xuống
 *   chứ không đè lên dòng đầu trang.
 */
function DaiCanhBaoNguy({ nguy }: { nguy: Alarm[] }) {
  if (nguy.length === 0) return null;

  const dau = nguy[0];
  // ⚠ `dau.mucDo` — mức RIÊNG của hàng ấy (YC-29). Bỏ nó ⇒ dải này lại đọc mức theo MÃ, và
  //   một `METER_TIMEOUT` trên tủ đã tắt bảo vệ sẽ leo lên dải nguy như cũ.
  const g = phanMuc(dau.code, dau.mucDo);

  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * GD-17 A3 (quyết dev_app, lee lật được — mặc định VÀNG): điều kiện hiện GIỮ NGUYÊN (đặc tả
   *   31 "chỉ mức 1", `locMucNguy`), chỉ đổi MÀU và CHỮ: một dòng vàng "N việc mức 1 · Xem",
   *   không chữ "nguy". Hệ chiếu sáng không phải hệ an toàn (lee, GD-16) — dải là LỐI VÀO bảng
   *   "Việc cần xem", không phải bản sao của cảnh báo (L-MỘT-LẦN).
   * ⛔ Vẫn NÓI RA hai ca bất thường, vì chúng đổi nghĩa của con số: mã chưa phân mức, và mã lẽ ra
   *   chưa có nguồn mà vẫn hiện (báo dev).
   * ⚠ `h-8` CỐ ĐỊNH: `aside`/`main` tụt xuống đúng chừng ấy.
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  return (
    <div className={`fixed inset-x-0 ${TOP_DAI[0]} z-30 flex h-8 items-center gap-2 border-b border-canh/40 bg-canh-nen px-3 text-chu-1 sm:px-4`}>
      <span className="min-w-0 flex-1 truncate text-sm">
        <span className="so-do font-semibold text-canh">{nguy.length}</span> việc mức 1
        {!g.biet && <span className="ml-1 text-chu-2">(có mã chưa phân mức)</span>}
        {/* YC-44: số đo đã cũ thì NÓI RA — dải không khẳng định thứ không còn đo lại được. */}
        {(() => {
          const c = cauDoTuoi(dau.doTuoi, (ms) => fmtTime(ms));
          return c ? <span className="ml-1 text-chu-2">({dau.cabinet?.name ?? 'tủ'}: {c})</span> : null;
        })()}
        {g.chuaCoNguon && (
          <span className="ml-1 text-chu-2">(mã này lẽ ra CHƯA CÓ NGUỒN — báo dev)</span>
        )}
      </span>
      <Link to="/" className="shrink-0 text-xs font-semibold text-nhan hover:underline">
        Xem
      </Link>
    </div>
  );
}

/* --------------------------------------------- 🧪 dải "ĐANG MÔ PHỎNG NGÕ VÀO" */

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * DẢI THƯỜNG TRỰC KHI MÁY CHỦ ĐANG MÔ PHỎNG NGÕ VÀO — điều kiện 3 của `services/mophong.ts`.
 *
 * dev_device: *"Luồng mô phỏng hai ngõ vào mà không có dải báo thì đúng ngày nào đó có người
 * đọc số mô phỏng như số thật. Cùng họ `manualHold` luôn `false`."*
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * ⛔ BỐN ĐIỀU CỐ Ý, MỖI ĐIỀU CHẶN MỘT ĐƯỜNG HỎNG CỤ THỂ:
 *
 * 1 · **KHÔNG có nút đóng, không có `useState`.** Dải Nguy tự biến mất khi hết nguy — nó nói
 *     về một SỰ KIỆN. Dải này nói về một ĐIỀU KIỆN của cả máy chủ: nó chỉ được tắt bằng cách
 *     tắt mô phỏng. Cho đóng được là cho người trực tự tay xoá thứ duy nhất bảo họ đừng tin
 *     số trên màn hình — và ca sau ngồi vào là không còn dấu nào.
 *
 * 2 · **Chữ `dau` lấy từ máy chủ, không gõ lại `[MÔ PHỎNG]` ở đây.** Một dấu hai nơi khai là
 *     một dấu sẽ lệch (xem `types.ts`).
 *
 * 3 · **Tên ngõ lấy từ `tt.ngoVao`, không viết tay.** Ngày dev_device thêm ngõ thứ ba, dải này
 *     tự kể đúng ba ngõ. Viết tay ⇒ dải khai hai ngõ trong khi ba ngõ đang sinh số giả.
 *
 * 4 · **`h-8` cố định + `truncate`** — cùng lý do đã ghi ở dải Nguy: `TOP_THEO_SO_DAI` chỉ đúng
 *     khi chiều cao là HẰNG.
 *
 * ⚠ CHỖ DẢI NÀY KHÔNG CỨU ĐƯỢC, nói thẳng ra: mất máy chủ ⇒ `tt` là `undefined` ⇒ dải biến mất,
 *   trong khi các cảnh báo mô phỏng cũ vẫn còn trong bộ đệm trình duyệt. Đó chính là lý do dấu
 *   `[MÔ PHỎNG]` nằm TRONG câu cảnh báo chứ không phải chỉ ở dải này — hai lớp, và lớp trong
 *   cùng đi theo câu chữ tới tận bản in. Đừng bao giờ rút lớp ấy vì "đã có dải rồi".
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
function DaiMoPhong({ tt, coNguy }: { tt: TrangThaiMoPhong | undefined; coNguy: boolean }) {
  if (tt?.bat !== true) return null;

  /*
   * ⚠ Danh sách rỗng phải NÓI RA, không được im. `bat = true` mà không khai ngõ nào nghĩa là
   *   máy chủ đang bật một cửa sinh cảnh báo mà giao diện không biết nó sinh cho cái gì — một
   *   câu "(máy chủ không khai ngõ nào)" khiến người đọc đi hỏi; một dải trống thì không.
   */
  const ten = tt.ngoVao.length > 0
    ? tt.ngoVao.map((n) => n.ten).join(' · ')
    : '(máy chủ không khai ngõ nào)';

  return (
    <div className={`fixed inset-x-0 ${TOP_DAI[coNguy ? 1 : 0]} z-30 flex h-8 items-center gap-2 border-b border-canh bg-canh px-3 text-nen-0 shadow-sm sm:px-4`}>
      <FlaskConical className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate text-sm font-semibold">
        {tt.dau} ĐANG MÔ PHỎNG NGÕ VÀO: {ten} — cảnh báo từ các ngõ này do MÁY CHỦ sinh ra để
        chạy thử luồng, không phải phép đo từ tủ.
      </span>
    </div>
  );
}

/* ------------------------------------------ 🥇 thanh trạng thái dưới */

/**
 * `● Máy chủ: NỐI │ Tủ 12/14 online │ ⚠ 2 cảnh báo │ Giờ máy chủ 15:42:07`
 *
 * ⚠ Mọi con số ở đây dùng `.so-do` *(tabular-nums)* — `12/14` phải **đứng yên** khi đổi,
 *   nếu không cả thanh nhấp nháy mỗi 30 giây và mắt thôi tin nó.
 *
 * ⚠ Nhãn giờ đổi theo `theoMayChu`: đo được lệch giờ ⇒ *"Giờ máy chủ"*; chưa đo được ⇒
 *   *"Giờ máy"* + xám nghiêng. **Không vẽ giờ trình duyệt dưới nhãn máy chủ.**
 */
/* ------------------------------------------- 🥇 ô "gõ để nhảy" (PHẦN 6.4a) */

interface MucNhay {
  /** `trang` · `duong` · `tu` — quyết định biểu tượng. */
  loai: 'trang' | 'duong' | 'tu';
  nhan: string;
  /** Dòng thứ hai, nhạt — mã, địa chỉ, trạng thái. Có thể rỗng. */
  phu?: string;
  to: string;
  /**
   * MÃ ĐỊNH DANH — thứ người ta gõ khi họ muốn **đúng cái này**: mã tủ, `hardwareId`, mã đường.
   * Khớp TIỀN TỐ ở đây thắng mọi khớp khác.
   */
  ma: string;
  /** Tên đọc được. Khớp tiền tố ở đây đứng hạng hai. */
  ten: string;
  /** Mọi thứ còn lại đem đi so: địa chỉ, nhãn cảnh báo đang mở, mã thiết bị. */
  them: string;
  /** Có cảnh báo mức 1 đang mở không — dùng để phá hoà, không dùng để lọc. */
  dangNguy: boolean;
}

/**
 * Ô GÕ ĐỂ NHẢY — `Ctrl+K`. Đặc tả `31_QUY_HOACH_APP.md` PHẦN 6.4a, việc 2 của PHẦN 7.3.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * 🎯 Vì sao nó nằm ở **thanh dưới** chứ không phải góc trên phải
 *
 * Qt Creator để `Type to locate (Ctrl+K)` ở góc **trái thanh dưới**. Ô tìm ở góc trên phải là
 * gu mạng xã hội — và đặc tả 6.0 đã liệt nó vào danh sách **BỎ**.
 *
 * > *"Ô gõ để nhảy là thứ biến một trang web thành một công cụ."*
 *
 * ⚠️ Thanh bên **vẫn phải đầy đủ**: nó là đường học của người mới. Ô này là **lối tắt**, không
 *   phải lối thay thế — nên nó không được phép giấu bớt thứ gì mà thanh bên đang có.
 *
 * ⛔ Và nó **KHÔNG thêm một màn hình nào**: không có `path=` mới, không có `<Route>` mới. Nó là
 *   một lớp phủ nhảy tới những trang đã có. Đó là lý do việc này chạy được trước 0–G.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
function ODeNhay({ mo, dong }: { mo: boolean; dong: () => void }) {
  const ttMuc = useTrangThaiMuc();
  const { data: routes } = useTree();
  const { data: canhBao } = useAlarms();
  const navigate = useNavigate();
  const [tu, setTu] = useState('');
  const [chon, setChon] = useState(0);
  const oNhap = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mo) { setTu(''); setChon(0); setTimeout(() => oNhap.current?.focus(), 0); }
  }, [mo]);

  /*
   * Nguồn: đúng những thứ thanh bên đã có, không hơn. Ô tắt mà chỉ ra được thứ thanh bên
   * không có thì hai chỗ đã trôi khỏi nhau — và người dùng học ở chỗ này rồi không tìm nổi
   * ở chỗ kia.
   */
  const tatCa = useMemo<MucNhay[]>(() => {
    // Nhãn cảnh báo ĐANG MỞ của từng tủ — để gõ "mất công tơ" ra được "cái nào đang hỏng".
    const nhanTheoTu = new Map<string, { chu: string[]; nguy: boolean }>();
    for (const a of canhBao ?? []) {
      const g = phanMuc(a.code, a.mucDo);
      const o = nhanTheoTu.get(a.cabinetId) ?? { chu: [], nguy: false };
      o.chu.push(g.nhan);
      if (g.mucDo === 1) o.nguy = true;
      nhanTheoTu.set(a.cabinetId, o);
    }

    const ra: MucNhay[] = [];
    for (const nhom of NHOM_NAV) {
      for (const n of nhom.muc) {
        /*
         * ⚠ Ô GÕ-ĐỂ-NHẢY CHỈ NHẬN MỤC `'mo'`. Đây KHÔNG phải "ẩn": ô này là một cái phím tắt,
         *   và một phím tắt nhảy vào trang báo "không đủ quyền" thì vô dụng hơn là không có.
         *   Mục khoá vẫn HIỆN ở thanh bên kèm ổ khoá — đó mới là chỗ trả lời câu "vì sao".
         * ⛔ `'chua-biet'` cũng không nhận: chưa đo được thì chưa mời người ta nhảy vào.
         */
        if (ttMuc(n) !== 'mo') continue;
        ra.push({
          loai: 'trang', nhan: n.label, phu: nhom.tieuDe, to: n.to,
          ma: '', ten: n.label.toLowerCase(), them: nhom.tieuDe.toLowerCase(), dangNguy: false,
        });
      }
    }
    for (const r of routes ?? []) {
      ra.push({
        loai: 'duong', nhan: r.tenDayDu, phu: `${r.onlineCount}/${r.cabinetCount} tủ trực tuyến`,
        to: '/dieu-khien',
        ma: (r.code ?? '').toLowerCase(),
        ten: r.tenDayDu.toLowerCase(),
        them: '',
        dangNguy: false,
      });
      for (const c of r.cabinets) {
        const cb = nhanTheoTu.get(c.id);
        ra.push({
          loai: 'tu', nhan: c.name,
          phu: cb ? `${c.code} · ${cb.chu[0]}` : `${c.code} · ${r.tenDayDu}`,
          to: `/tu/${c.id}`,
          // ⚠ `hardwareId` đứng trong MÃ ĐỊNH DANH: kỹ thuật viên ngoài hiện trường đọc mã
          //   trên vỏ tủ, và đó thường là thứ DUY NHẤT họ có trong tay lúc đứng dưới cột đèn.
          ma: `${c.code} ${c.hardwareId}`.toLowerCase(),
          ten: c.name.toLowerCase(),
          them: `${r.tenDayDu} ${c.deviceId ?? ''} ${(cb?.chu ?? []).join(' ')}`.toLowerCase(),
          dangNguy: cb?.nguy ?? false,
        });
      }
    }
    return ra;
  }, [routes, ttMuc, canhBao]);

  const kq = useMemo(() => {
    const k = tu.trim().toLowerCase();
    if (!k) return tatCa.filter((m) => m.loai === 'trang');
    /*
     * ⚠ `thuTu` = vị trí trong cây, và nó CHỈ dùng để phá hoà ở nấc thứ ba. Bản đầu dùng nó
     *   làm thứ tự duy nhất — gõ một từ chung chung thì kết quả xếp vô nghĩa.
     */
    return tatCa
      .map((m, thuTu) => ({ m, thuTu, hang: hangKhop(m, k), dangNguy: m.dangNguy }))
      .filter((x) => x.hang !== 9)
      .sort(soSanhNhay)
      .slice(0, 40)
      .map((x) => x.m);
  }, [tu, tatCa]);

  useEffect(() => { setChon(0); }, [tu]);

  if (!mo) return null;

  const nhay = (m?: MucNhay) => { if (m) { navigate(m.to); dong(); } };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh]" role="dialog" aria-modal="true" aria-label="Gõ để nhảy">
      <div className="absolute inset-0 bg-nen-0/80" onClick={dong} />
      <div className="relative w-[92%] max-w-xl overflow-hidden rounded-the border border-vien-manh bg-nen-1 shadow-xl">
        <div className="flex items-center gap-2 border-b border-vien px-3">
          <Search className="h-4 w-4 shrink-0 text-chu-3" />
          <input
            ref={oNhap}
            value={tu}
            onChange={(e) => setTu(e.target.value)}
            placeholder="Gõ tên tủ, mã tủ, mã phần cứng, tên đường, hoặc tên trang…"
            className="w-full bg-transparent py-3 text-sm text-chu-1 outline-none placeholder:text-chu-3"
            onKeyDown={(e) => {
              if (e.key === 'Escape') { e.preventDefault(); dong(); }
              else if (e.key === 'ArrowDown') { e.preventDefault(); setChon((v) => Math.min(v + 1, kq.length - 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setChon((v) => Math.max(v - 1, 0)); }
              else if (e.key === 'Enter') { e.preventDefault(); nhay(kq[chon]); }
            }}
          />
          <kbd className="hidden shrink-0 rounded border border-vien px-1.5 py-0.5 text-xs text-chu-3 sm:block">Esc</kbd>
        </div>

        <div className="thin-scroll max-h-[52vh] overflow-y-auto py-1">
          {kq.length === 0 && (
            // ⚠ Nói ra là KHÔNG TÌM THẤY. Một danh sách rỗng không chú thích thì người dùng
            //   tưởng ô hỏng — cùng luật với "chưa có số liệu" của `Metric`.
            <p className="px-3 py-6 text-center text-sm text-chu-3">
              Không có tủ, đường hay trang nào khớp “{tu}”.
            </p>
          )}
          {kq.map((m, i) => (
            <button
              key={`${m.loai}-${m.to}-${i}`}
              onMouseEnter={() => setChon(i)}
              onClick={() => nhay(m)}
              className={[
                'flex w-full items-center gap-2.5 px-3 py-2 text-left',
                i === chon ? 'bg-nhan/15' : 'hover:bg-nen-2',
              ].join(' ')}
            >
              {m.loai === 'trang' ? <LayoutDashboard className="h-4 w-4 shrink-0 text-chu-3" />
                : m.loai === 'duong' ? <MapPin className="h-4 w-4 shrink-0 text-chu-3" />
                : <Zap className="h-4 w-4 shrink-0 text-chu-3" />}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-chu-1">{m.nhan}</span>
                {m.phu && <span className="block truncate text-xs text-chu-3">{m.phu}</span>}
              </span>
              {/* Mức NGUY hiện thành MỘT CHẤM, không phải một màu chữ khác — ba mức là ba
                  HÌNH DẠNG (PHẦN 2 luật 3), và ở đây chỉ cần phân biệt "có" với "không". */}
              {m.dangNguy && <AlertOctagon className="h-3.5 w-3.5 shrink-0 text-nguy" />}
              <span className="shrink-0 text-xs uppercase tracking-wide text-chu-3">
                {m.loai === 'trang' ? 'trang' : m.loai === 'duong' ? 'đường' : 'tủ'}
              </span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 border-t border-vien px-3 py-1.5 text-xs text-chu-3">
          <span>↑↓ chọn</span><span>↵ nhảy tới</span><span>Esc đóng</span>
          <span className="ml-auto so-do">{kq.length} kết quả</span>
        </div>
      </div>
    </div>
  );
}

function ThanhTrangThai({ moODeNhay }: { moODeNhay: () => void }) {
  const status = useRealtimeStatus();
  const { data: ov } = useOverview();
  const { data: alarms } = useAlarms();
  const { gio, theoMayChu } = useGioMayChu();

  const nguy = locMucNguy(alarms).length;
  const tongCanhBao = alarms?.length ?? ov?.activeAlarms ?? null;

  const chuMayChu = status === 'open' ? 'NỐI' : status === 'connecting' ? 'ĐANG NỐI' : 'MẤT';

  return (
    <footer
      className={`fixed inset-x-0 bottom-0 z-40 flex ${CAO_THANH_DUOI} items-center gap-3 border-t border-vien-manh bg-nen-0 px-3 text-xs text-chu-3 sm:gap-4 sm:px-4`}
      role="status"
      aria-live="off"
    >
      {/*
        ⚠ CHỮ RÚT NGẮN TRÊN ĐIỆN THOẠI, KHÔNG BỎ BỚT MỤC NÀO.
        Ảnh chụp 390px cho thấy thanh này TRÀN và **giờ bị cắt cụt bên phải**. Cách chữa
        KHÔNG phải là bỏ đồng hồ đi — bỏ đi là mất đúng cái thứ làm nó ra dáng phần mềm.
        Rút nhãn, giữ đủ bốn con số.
      */}
      {/*
        🥇 Ô "gõ để nhảy" — GÓC TRÁI thanh dưới, đúng chỗ Qt Creator để nó.
        ⚠ Trên điện thoại chỉ còn cái kính lúp: chỗ ở đây quý, và người dùng điện thoại
          không có bàn phím để gõ Ctrl+K — họ bấm.
      */}
      <button
        onClick={moODeNhay}
        title="Gõ để nhảy tới một tủ, một tuyến chiếu sáng, hay một trang (Ctrl+K)"
        className="flex shrink-0 items-center gap-1.5 rounded-nut border border-vien px-2 py-0.5 text-chu-2 hover:border-vien-manh hover:text-chu-1"
      >
        <Search className="h-3 w-3" />
        <span className="hidden sm:inline">Gõ để nhảy</span>
        <kbd className="hidden border-l border-vien pl-1.5 text-xs text-chu-3 sm:inline">Ctrl+K</kbd>
      </button>

      <span className="hidden shrink-0 text-chu-2 sm:inline">│</span>

      <span className="flex shrink-0 items-center gap-1.5" title="Kênh realtime tới máy chủ">
        <StatusDot online={status === 'open'} size="sm" pulse={status !== 'closed'} />
        <span className="hidden sm:inline">Máy chủ:</span>
        <b className={status === 'open' ? 'text-chu-1' : 'text-canh'}>{chuMayChu}</b>
      </span>

      <span className="hidden shrink-0 text-chu-2 sm:inline">│</span>

      <span className="shrink-0" title="Số tủ đang trực tuyến trên tổng số tủ">
        <span className="hidden sm:inline">Tủ </span>
        <span className="so-do text-chu-1">{ov ? `${ov.online}/${ov.cabinets}` : '—/—'}</span>
        <span className="hidden sm:inline"> online</span>
      </span>

      <span className="hidden shrink-0 text-chu-2 sm:inline">│</span>

      {/* GD-17 A3: XÁM, chỉ số — không đỏ, không chữ "nguy". Số mức 1 ở dải vàng phía trên. */}
      <span className="shrink-0"
            title={`${tongCanhBao ?? 0} cảnh báo đang mở${nguy > 0 ? `, trong đó ${nguy} ở mức 1` : ''}`}>
        <span className="so-do">{tongCanhBao ?? '—'}</span>
        <span className="hidden sm:inline"> cảnh báo</span>
      </span>

      <span className="ml-auto shrink-0 pl-2" title={theoMayChu
        ? 'Lấy từ header Date của phản hồi API — sai số ~±1 giây'
        : 'CHƯA đọc được giờ máy chủ. Đây là giờ của máy này.'}>
        {theoMayChu
          ? <><span className="hidden sm:inline">Giờ máy chủ </span><span className="so-do text-chu-1">{gio}</span></>
          : <span className={LOP_CHUA_RO}><span className="hidden sm:inline">Giờ máy </span><span className="so-do">{gio}</span></span>}
      </span>
    </footer>
  );
}

/* ------------------------------------------------------------ thanh trên */

function TopBar({ onMenu, moODeNhay }: { onMenu: () => void; moODeNhay: () => void }) {
  const { user, signOut } = useAuth();
  const status = useRealtimeStatus();
  const { data: canhBao } = useAlarms();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const navigate = useNavigate();

  const statusText = status === 'open'
    ? 'Realtime đang chạy'
    : status === 'connecting' ? 'Đang kết nối realtime…' : 'Mất kết nối realtime';
  const soCanhBao = (canhBao ?? []).length;

  return (
    <header className="sticky top-0 z-30 flex h-[calc(3.5rem+env(safe-area-inset-top))] items-center gap-2 border-b border-vien bg-nen-1 px-3 pt-[env(safe-area-inset-top)] sm:px-4">
      <button onClick={onMenu} className="rounded-lg p-2 text-chu-2 hover:bg-nen-2 lg:hidden" aria-label="Mở menu">
        <Menu className="h-5 w-5" />
      </button>

      {/*
        * ➕ `GD-02` — Ô Ctrl+K RỘNG theo mẫu, thay cho tên app lặp lại (tên đã ở logo thanh bên).
        * ⛔ LOGIC GIỮ NGUYÊN: nó vẫn chỉ MỞ `ODeNhay` — cùng một ô gõ-để-nhảy, cùng nguồn dữ
        *   liệu, cùng phím tắt. Đây là một cái nút trông như ô nhập, không phải một ô nhập thứ
        *   hai: hai ô tìm là hai chỗ sẽ trôi khỏi nhau.
        */}
      <button
        onClick={moODeNhay}
        className="ml-1 flex h-10 min-w-0 flex-1 items-center gap-2 rounded-[14px] border border-vien bg-nen-2 px-4 text-left text-sm text-chu-3 transition hover:border-vien-manh hover:text-chu-2"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="truncate">Tìm tủ, tuyến, cảnh báo… (Ctrl+K)</span>
      </button>

      <div className="ml-auto flex items-center gap-2">
        <span
          className="hidden items-center gap-1.5 rounded-full bg-nen-2 px-2.5 py-1 text-xs text-chu-2 sm:inline-flex"
          title={statusText}
        >
          <StatusDot online={status === 'open'} size="sm" pulse />
          {status === 'open' ? 'Trực tuyến' : status === 'connecting' ? 'Đang nối…' : 'Mất kết nối'}
        </span>

        {/*
          * CHUÔNG CẢNH BÁO (mẫu). Bấm vào đi tới Tổng quan — nơi bảng cảnh báo sống.
          * ⛔ Con số là số cảnh báo ĐANG MỞ đang tải được, và nó KHÔNG hiện khi bằng 0: một
          *   chuông luôn có số bên cạnh là một chuông mắt thôi nhìn (cùng luật với dải NGUY).
          */}
        <Link
          to="/"
          title={soCanhBao > 0 ? `${soCanhBao} cảnh báo đang mở` : 'Không có cảnh báo đang mở'}
          className="relative flex h-10 w-10 items-center justify-center rounded-nut border border-vien bg-nen-2 text-chu-2 transition hover:text-chu-1"
        >
          <BellRing className="h-[18px] w-[18px]" strokeWidth={1.8} />
          {soCanhBao > 0 && (
            <span className="absolute -right-1 -top-1 min-w-[18px] rounded-thuoc border border-vien bg-nen-3 px-1 text-center text-xs font-semibold leading-[18px] text-chu-1">
              {soCanhBao}
            </span>
          )}
        </Link>

        <div className="relative">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-nen-2"
          >
            {/* Avatar TRÒN PHẲNG (GD-17: bỏ dải cam→đỏ — không đỏ trên Tổng quan, dải màu chỉ ở nền + nút chính). */}
            <span className="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-nen-3 text-xs font-semibold text-chu-1">
              {(user?.fullName || user?.username || '?').slice(0, 1).toUpperCase()}
            </span>
            <span className="hidden text-left sm:block">
              {/* Tên người dùng dùng bộ TIÊU ĐỀ — một trong bốn chỗ Playfair được phép. */}
              <span className="chu-tieude block text-[13px] font-semibold leading-tight text-chu-1">{user?.fullName}</span>
              <span className="block text-xs leading-tight text-chu-2">
                @{user?.username} · {status === 'open' ? 'trực tuyến' : 'mất kết nối'}
              </span>
            </span>
            <ChevronDown className="h-4 w-4 text-chu-3" />
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 z-20 mt-1 w-60 overflow-hidden rounded-xl border border-vien bg-nen-1 shadow-lg">
                <div className="border-b border-vien px-3 py-2">
                  <p className="text-sm font-medium text-chu-1">{user?.fullName}</p>
                  <p className="text-xs text-chu-2">@{user?.username}</p>
                  <Badge tone="blue" className="mt-1">{user ? ROLE_LABEL[user.role] : ''}</Badge>
                </div>
                {/*
                  * ═══════════════════════════════════════════════════════════════════════
                  * ➕ `GD-02` — MỤC "GIAO DIỆN": hai câu hỏi ĐỘC LẬP, hai hàng công tắc.
                  *
                  * ⛔ KHÔNG đóng menu sau khi bấm. Đây là chỗ người ta **thử rồi nhìn**:
                  *   bấm Sáng, liếc màn hình, bấm lại Tối. Đóng menu mỗi lần bấm là bắt họ
                  *   mở lại ba lần cho một quyết định.
                  * ⚠ Và hai hàng KHÔNG gộp thành một danh sách bốn lựa chọn: chế độ và bộ
                  *   chữ là hai trục, gộp lại là bắt đổi cả hai khi chỉ muốn đổi một.
                  * ═══════════════════════════════════════════════════════════════════════
                  */}
                <div className="border-b border-vien px-3 py-2">
                  <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-chu-3">
                    <Palette className="h-3.5 w-3.5" /> Giao diện
                  </p>
                  <ChonGiaoDien />
                </div>
                <button
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-chu-1 hover:bg-nen-2"
                  onClick={() => { setMenuOpen(false); setPwdOpen(true); }}
                >
                  <KeyRound className="h-4 w-4 text-chu-3" /> Đổi mật khẩu
                </button>
                <button
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-nguy hover:bg-nguy-nen"
                  onClick={async () => { setMenuOpen(false); await signOut(); navigate('/dang-nhap'); }}
                >
                  <LogOut className="h-4 w-4" /> Đăng xuất
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <ChangePasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </header>
  );
}

/**
 * Hai hàng công tắc: chế độ và bộ chữ. Nhãn lấy từ `lib/giaodien.tsx` — một chỗ viết.
 *
 * ⚠ `aria-pressed` chứ không `aria-checked`: đây là hai nhóm NÚT BẬT, không phải hộp kiểm.
 *   Trình đọc màn hình đọc ra "đã chọn / chưa chọn" đúng nghĩa người dùng đang gặp.
 */
function ChonGiaoDien() {
  const { cheDo, boChu, datCheDo, datBoChu } = useGiaoDien();
  const o = (dangChon: boolean) =>
    `flex-1 rounded-nut px-2 py-1.5 text-xs transition ${
      dangChon ? 'bg-nhan text-chu-1' : 'bg-nen-2 text-chu-2 hover:text-chu-1'}`;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-1.5">
        {(['toi', 'sang'] as CheDo[]).map((v) => (
          <button key={v} type="button" aria-pressed={cheDo === v} className={o(cheDo === v)}
            onClick={() => datCheDo(v)}>
            <span className="flex items-center justify-center gap-1.5">
              {v === 'toi' ? <Moon className="h-3.5 w-3.5" /> : <Sun className="h-3.5 w-3.5" />}
              {NHAN_CHE_DO[v]}
            </span>
          </button>
        ))}
      </div>
      <div className="flex gap-1.5">
        {(['lexend', 'bevietnam'] as BoChu[]).map((v) => (
          <button key={v} type="button" aria-pressed={boChu === v} className={o(boChu === v)}
            onClick={() => datBoChu(v)}>
            <span className="flex items-center justify-center gap-1.5">
              <Type className="h-3.5 w-3.5" />
              {NHAN_BO_CHU[v]}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------- đổi mật khẩu */

export function ChangePasswordModal({
  open, onClose, forced = false,
}: { open: boolean; onClose: () => void; forced?: boolean }) {
  const { changePassword } = useAuth();
  const toast = useToast();
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    if (newPwd.length < 6) return setErr('Mật khẩu mới phải từ 6 ký tự trở lên');
    if (newPwd !== confirm) return setErr('Hai lần nhập mật khẩu mới không khớp');
    setBusy(true);
    try {
      await changePassword(oldPwd, newPwd);
      toast.success('Đã đổi mật khẩu');
      setOldPwd(''); setNewPwd(''); setConfirm('');
      onClose();
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={forced ? () => { /* bắt buộc đổi, không cho đóng */ } : onClose}
      hoiKhiDong={!forced} /* TK-27: bắt buộc đổi thì hộp không đóng được — hỏi "bỏ?" là hỏi thừa */
      title="Đổi mật khẩu"
      subtitle={forced ? 'Tài khoản đang dùng mật khẩu tạm — bắt buộc đổi trước khi vào hệ thống.' : undefined}
      footer={
        <>
          {!forced && <Button variant="secondary" onClick={onClose} disabled={busy}>Huỷ</Button>}
          <Button variant="primary" onClick={submit} loading={busy}>Lưu mật khẩu mới</Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Mật khẩu hiện tại" required>
          <input className="inp" type="password" value={oldPwd} autoComplete="current-password"
            onChange={(e) => setOldPwd(e.target.value)} />
        </Field>
        <Field label="Mật khẩu mới" required hint="Tối thiểu 6 ký tự">
          <input className="inp" type="password" value={newPwd} autoComplete="new-password"
            onChange={(e) => setNewPwd(e.target.value)} />
        </Field>
        <Field label="Nhập lại mật khẩu mới" required error={err ?? undefined}>
          <input className="inp" type="password" value={confirm} autoComplete="new-password"
            onChange={(e) => setConfirm(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }} />
        </Field>
      </div>
    </Modal>
  );
}

/* --------------------------------------------------------- thanh bên */

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * THANH RAIL — icon lớn, nhãn nhỏ phía dưới (lee chốt 05/09, theo mẫu anh gửi).
 *
 * ⚠ Rail rộng 88px chở được ~10 ký tự một dòng, nên mỗi mục dùng `nhanRail` (chữ tắt có
 *   chủ) chứ KHÔNG cắt `label`. Tên đầy đủ vẫn tới người dùng qua `title` khi rê chuột và
 *   qua ô gõ-để-nhảy — xem ghi chú ở `NavItem.nhanRail`.
 *
 * ⛔ Tiêu đề nhóm (GIÁM SÁT / ĐIỀU KHIỂN / …) BỎ khỏi rail: 88px không đủ cho chúng, và một
 *   tiêu đề bị cắt còn tệ hơn không có. Thay bằng một đường kẻ mảnh giữa hai nhóm — cùng
 *   công dụng phân cụm, không tốn bề ngang. Nhóm nào rỗng vì phân quyền thì KHÔNG kẻ, để
 *   đường kẻ không trở thành một cụm rỗng.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * THANH BÊN CHÍNH — 200 px, theo mẫu lee duyệt (`GD-02`).
 *
 * Bốn tầng, đúng thứ tự của mẫu:
 *     logo  ·  danh sách mục  ·  thẻ xanh "tủ gần nhất"  ·  Đăng xuất
 *
 * ⛔ THAY CHO RAIL 88 px + NHÃN VIẾT TẮT. Và đó là một thứ **lấy lại được** chứ không chỉ đổi
 *   dáng: rail cũ phải mang `nhanRail` — một cái tên viết tắt thứ hai cho cùng một mục, tồn
 *   tại chỉ vì 88 px không chở nổi *"Quản lý Tuyến chiếu sáng"*. 200 px chở được tên thật.
 * ⚠ `nhanRail` GIỮ NGUYÊN trong `NavItem` (ô gõ-để-nhảy vẫn đọc nó và các nhóm vẫn khai nó);
 *   chỉ thanh bên thôi dùng. Xoá một trường đang có người đọc là một việc khác, cho một phiếu
 *   khác.
 *
 * ⚠ CÁC NHÓM (`NHOM_NAV`) vẫn còn — chúng phân quyền và gom việc — nhưng mẫu vẽ một danh sách
 *   PHẲNG, nên tiêu đề nhóm không hiện. Ranh giới nhóm còn lại một đường kẻ mảnh.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
/**
 * 🔴 `giua` — CHỖ CHÈN GIỮA nav và khối đáy ("Đang mở" / Đăng xuất / phiên bản).
 *
 * ⛔ VÌ SAO PHẢI LÀ MỘT CHỖ CHÈN chứ không phải dựng cây SAU thành phần này: khối đáy mang
 *   `mt-auto`, nên trong ngăn kéo điện thoại `ThanhBenChinh` ăn hết chiều cao và mọi thứ
 *   dựng sau nó bị đẩy khỏi tầm nhìn. Lee báo đúng triệu chứng ấy: *"Không tìm thấy danh
 *   sách các tủ theo tuyến trên giao diện ipad, phone"* — cây CÓ dựng, chỉ là không ai cuộn
 *   tới. Một thứ phải cuộn mới thấy, trên màn hình người ta cầm một tay ngoài hiện trường,
 *   là một thứ không có.
 */
function ThanhBenChinh({ giua }: { giua?: ReactNode }) {
  const ttMuc = useTrangThaiMuc();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { data: routes } = useTree();
  const gan = tuGanNhat(routes);

  /*
   * ⛔ `GD-09` mục 4 — KHÔNG LỌC NỮA. Mọi nhóm, mọi mục đều hiện; trạng thái đi kèm từng mục.
   *   Trước bản này nhóm rỗng vì phân quyền thì biến mất, và menu của hai người khác nhau có
   *   số dòng khác nhau — không ai đối chiếu được với ai khi cùng đọc một hướng dẫn.
   */
  const nhomThay = NHOM_NAV.map((n) => ({ ...n, muc: n.muc.map((m) => ({ m, tt: ttMuc(m) })) }));

  return (
    <nav className="thin-scroll flex h-full min-h-0 flex-col gap-1 overflow-y-auto p-3">
      {/* Logo — ô vuông bo góc chuyển sắc xanh→tím, đúng mẫu. Chữ dùng bộ TIÊU ĐỀ. */}
      <Link to="/" className="mb-3 flex items-center gap-2.5 px-2 pt-1">
        <span className="h-[26px] w-[26px] shrink-0 rounded-lg bg-gradient-to-br from-nhan to-tim" />
        <span className="chu-tieude text-[15px] font-bold text-chu-1">SDKD Chiếu sáng</span>
      </Link>

      {nhomThay.map((nhom, i) => (
        <div key={nhom.tieuDe}>
          {i > 0 && <div className="mx-2 my-1 border-t border-vien" />}
          {nhom.muc.map(({ m: n, tt }) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              title={tt === 'khoa' ? `${n.label} — bạn chưa được cấp quyền này` : n.label}
              className={({ isActive }) =>
                [
                  'flex items-center gap-2.5 rounded-nut px-3 py-2.5 text-[14px] transition',
                  /* ⛔ `text-chu-1`, KHÔNG `text-white`: một mã màu viết thẳng là một chỗ
                     KHÔNG đổi theo diện mạo — đúng thứ phép [39] chặn. */
                  isActive ? 'bg-nen-2 text-chu-1' : 'text-chu-2 hover:bg-nen-2 hover:text-chu-1',
                  /*
                   * ⚠ MỤC KHOÁ VẪN BẤM ĐƯỢC — và đó là chủ ý: trang đích nói rõ THIẾU TICK NÀO
                   *   và AI cấp được, còn một cái menu thì không đủ chỗ cho câu ấy. Mờ đi là để
                   *   mắt phân biệt, không phải để chặn.
                   */
                  tt === 'mo' ? '' : 'opacity-60',
                ].join(' ')
              }
            >
              {({ isActive }) => (
                <>
                  {/*
                    * ⚠ 22 px NÉT MẢNH (mẫu: `stroke-width 1.8`) — `lucide-react` mặc định 2.
                    *   Nét mảnh là thứ tách "dáng Windows 11" khỏi "dáng bảng điều khiển web";
                    *   ở cỡ 22 px thì nét 2 trông nặng và các icon dính vào nhau.
                    * ⛔ Icon của mục ĐANG CHỌN đổi sang màu nhận (`nhan`), còn NỀN là `nen-2` —
                    *   hai thứ khác nhau, đúng mẫu. Trước đây cả ô nền là `bg-nhan`, và nó át
                    *   hết chữ.
                    */}
                  <n.icon
                    strokeWidth={1.8}
                    className={`h-[22px] w-[22px] shrink-0 ${isActive ? 'text-nhan' : ''}`}
                  />
                  <span className="truncate">{n.label}</span>
                  <DauTrangThaiMuc tt={tt} />
                </>
              )}
            </NavLink>
          ))}
        </div>
      ))}

      {giua}

      {/*
        * ═══════════════════════════════════════════════════════════════════════════════
        * THẺ XANH ĐÁY THANH — "tủ gần nhất", theo mẫu.
        *
        * ⛔ KHÔNG CÓ TỦ NÀO ⇒ **ẨN HẲN THẺ** (phiếu chốt). Một thẻ xanh ghi "—" ở chỗ tên tủ
        *   là một thẻ trông như đang tải dở, và người mới dựng hệ thống sẽ đi tìm xem mình
        *   thiếu gì. Không có tủ thì không có gì để nói.
        * ⚠ "Gần nhất" = gần nhất VỀ THỜI GIAN, và định nghĩa ấy nằm ở `lib/tugannhat.ts` —
        *   dùng chung với thẻ "Tủ gần nhất" của Tổng quan, để hai chỗ không chỉ hai tủ khác nhau.
        * ⚠ `mt-auto` đẩy thẻ xuống đáy — đúng chỗ mẫu đặt nó.
        * ═══════════════════════════════════════════════════════════════════════════════
        */}
      {gan && (
        /*
         * ⛔ `from-nhan to-nhan-dam` + `text-tren-nhan`, KHÔNG `from-brand-600 to-brand-700`
         *   + `text-white`. Bản đầu của tôi viết đúng bốn lớp cũ ấy và phép [39] đếm ra **5
         *   chỗ viết thẳng mã màu** — đúng năm chỗ sẽ đứng im khi lee gạt sang chế độ sáng,
         *   trong khi cả thanh bên quanh nó đổi. Một thẻ xanh đậm nằm giữa một trang trắng.
         * ⚠ `text-tren-nhan` chứ không `text-chu-1`: xem `thietke.css` — chữ TRÊN NỀN NHẤN là
         *   một vai trò riêng, và ở chế độ sáng `--chu-1` là `#111827`.
         * ⚠ CHÚ THÍCH KIỂU JS, KHÔNG bọc trong ngoặc nhọn: chỗ này nằm **trong** biểu thức
         *   `{gan && ( … )}`, nên một khối ngoặc nhọn thứ hai ở đây là một phần tử JSX thứ hai
         *   đứng cạnh — cú pháp hỏng. Mục `[0]` của bộ kiểm bắt đúng lỗi ấy trước mọi phép khác.
         */
        /*
         * GD-16 mục 8 — BỎ nền đậm: khối tủ giờ là MỘT dòng chữ thường (L-NGÂN-SÁCH-MÀU: không
         *   tô nền). "Đang mở: <tủ> · <trạng thái> · <phiên bản>" + liên kết "Xem tủ".
         */
        /*
         * 🔴 09/10/2026 — lee khoanh đỏ khối này. Ở bề rộng thanh bên, chuỗi phần sụn đầy đủ
         *   ("2.1.0-beta1+b8f39d0 Oct 6 2026") rớt thành BỐN dòng và đẩy nút Đăng xuất xuống.
         *   Phần sụn đã in sẵn ở đầu trang chi tiết tủ — in lại ở đây là in hai lần cùng một
         *   thứ, và chỗ in lại là chỗ hẹp nhất màn hình. Chuyển vào `title`.
         * ⚠ Chấm trạng thái thay cho chữ "· đang nối / · mất kết nối": cùng tin, một ký tự.
         */
        <div
          className="mt-auto border-t border-vien pt-3 text-xs text-chu-2"
          title={gan.tu.firmwareVersion ? `Phần sụn ${gan.tu.firmwareVersion}` : undefined}
        >
          <StatusDot online={gan.tu.status === 'ONLINE'} size="sm" />
          <b className="ml-1.5 font-semibold text-chu-1">{gan.tu.name}</b>
          <Link to={`/tu/${gan.tu.id}`} className="ml-2 text-nhan hover:underline">Xem tủ</Link>
        </div>
      )}

      <button
        onClick={async () => { await signOut(); navigate('/dang-nhap'); }}
        title={user ? `Đăng xuất @${user.username}` : 'Đăng xuất'}
        className="mt-2 flex items-center gap-2.5 rounded-nut px-3 py-2.5 text-[14px] text-chu-2 transition hover:bg-nen-2 hover:text-chu-1"
      >
        <LogOut strokeWidth={1.8} className="h-[22px] w-[22px] shrink-0" />
        Đăng xuất
      </button>

      <TranhThanhPho />
    </nav>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ➕ `GD-07A` 11/09/2026 — TRANH THÀNH PHỐ + KHẨU HIỆU + PHIÊN BẢN, đáy thanh trái (mẫu `27`).
 *
 * 🔴 VÌ SAO MỘT BỨC TRANH LẠI ĐÁNG CHỖ TRONG MỘT CÔNG CỤ VẬN HÀNH: nó không phải trang trí —
 *   nó là chỗ **neo phiên bản**. Người trực gọi điện báo lỗi thì câu đầu tiên phải trả lời là
 *   *"anh đang chạy bản nào"*, và cho tới hôm nay con số ấy không có mặt ở đâu trên màn hình.
 *   Ba đèn đường sáng trong tranh chỉ là cách làm cho cái chỗ ấy có người nhìn tới.
 *
 * ⛔ SỐ PHIÊN BẢN ĐỌC TỪ `package.json`, KHÔNG GÕ TAY. Một con số gõ tay ở đây là con số sẽ
 *   nói dối ngay lần phát hành sau — và nó nói dối đúng vào lúc người ta tin nó nhất.
 * ⚠ `resolveJsonModule` đã bật sẵn trong `tsconfig`; Vite nhúng thẳng lúc dựng gói, không có
 *   lời gọi mạng nào.
 *
 * ⛔ MÀU TRONG SVG ĐI QUA TOKEN, không một mã màu gõ tay: `currentColor` + `text-*`, và hai
 *   `stop-color` của dải dùng `rgb(var(--nhan) / …)`. Bức tranh này nằm trong `Layout.tsx`,
 *   tệp mà phép `[39]` đếm — một `#16305c` ở đây là một chỗ đứng im khi lee gạt sang chế độ
 *   sáng, và nó sẽ là cái vệt tối duy nhất trên một thanh bên trắng.
 * ⚠ `aria-hidden` + `focusable="false"`: trình đọc màn hình không có gì để nói về một bức
 *   tranh trang trí, còn khẩu hiệu và số phiên bản là chữ thật nên chúng vẫn đọc được.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
/*
 * GD-16 mục 8 — BỎ tranh minh hoạ + khẩu hiệu "Thắp sáng những con đường xanh" khỏi thanh bên
 *   (được đưa sang trang đăng nhập). GIỮ số phiên bản: người trực gọi báo lỗi cần biết bản nào,
 *   và đó là chữ thật, không phải trang trí (L-KHÔNG-PHỒNG cấm minh hoạ, không cấm số phiên bản).
 */
function TranhThanhPho() {
  return (
    <p className="mt-3 px-1.5 pb-1 text-xs text-chu-3">v{PHIEN_BAN}</p>
  );
}

/**
 * ⚠ `hienNav` — phần điều hướng nay nằm ở `ThanhBenChinh`, nên cột cây CHỈ chở
 *   cây. Trong ngăn kéo điện thoại thì không có rail, nên vẫn chở cả hai.
 *   Cố ý MỘT thành phần với một cờ, không phải hai bản chép: hai bản chép là hai chỗ sẽ
 *   trôi khỏi nhau, và cây tuyến đường là thứ vừa đổi tên cả một lượt.
 */
function SidebarContent({ hienNav = true }: { hienNav?: boolean }) {
  const ttMuc = useTrangThaiMuc();
  const { data: routes, isLoading, isError } = useTree();
  const [keyword, setKeyword] = useState('');

  const filtered = useMemo(() => filterTree(routes ?? [], keyword), [routes, keyword]);

  return (
    // `h-full` + `min-h-0 flex-1`: chiều cao do CHA quyết định — `aside` cố định
    // (`top-14` → `bottom-7`) trên màn hình lớn, ngăn kéo toàn màn hình trên điện thoại.
    // Bản cũ ghi cứng `calc(100vh-3.5rem)`, nên trong ngăn kéo nó luôn cao dư đúng 3.5rem.
    <div className="thin-scroll flex h-full min-h-0 flex-1 flex-col overflow-y-auto">
      {hienNav && <nav className="p-2">
        {NHOM_NAV.map((nhom) => {
          /*
           * ⚠ `GD-09` mục 4 — KHÔNG còn ẩn nhóm. Câu cũ ở đây nói *"một tiêu đề trống nói rằng
           *   có gì đó hỏng"* — đúng, và cách chữa của nó sai: nhóm nay KHÔNG BAO GIỜ rỗng, vì
           *   mục khoá vẫn hiện kèm ổ khoá. Không còn tiêu đề trống nào để phải giấu.
           */
          const thay = nhom.muc.map((m) => ({ m, tt: ttMuc(m) }));
          return (
            <div key={nhom.tieuDe} className="mb-2 last:mb-0">
              <p className="px-3 pb-1 pt-1 text-xs font-semibold uppercase tracking-wider text-chu-3">
                {nhom.tieuDe}
              </p>
              <div className="space-y-0.5">
                {thay.map(({ m: n, tt }) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    title={tt === 'khoa' ? `${n.label} — bạn chưa được cấp quyền này` : n.label}
                    className={({ isActive }) =>
                      [
                        // 40px trên điện thoại (một tay, ngoài hiện trường) · 32px từ `lg`
                        // trở lên (mật độ — thanh bên phần mềm thì dày và hẹp).
                        'flex min-h-[40px] items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition',
                        'lg:min-h-[32px] lg:py-1 lg:text-[13px]',
                        isActive ? 'bg-nhan/15 text-nhan' : 'text-chu-2 hover:bg-nen-2 hover:text-chu-1',
                        tt === 'mo' ? '' : 'opacity-60',
                      ].join(' ')
                    }
                  >
                    <n.icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{n.label}</span>
                    <DauTrangThaiMuc tt={tt} />
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>}

      <div className="border-t border-vien px-3 pb-2 pt-3">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-chu-3">
          <Activity className="h-3.5 w-3.5" /> Cây tuyến đường
        </p>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-chu-3" />
          <input
            className="inp py-1.5 pl-8 text-sm"
            placeholder="Tìm tuyến hoặc tủ…"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>
      </div>

      <div className="flex-1 px-2 pb-6">
        {isLoading && <p className="px-2 py-3 text-xs text-chu-3">Đang tải cây tuyến đường…</p>}
        {isError && <p className="px-2 py-3 text-xs text-nguy">Không tải được cây tuyến đường</p>}
        {!isLoading && !isError && filtered.length === 0 && (
          <p className="px-2 py-3 text-xs text-chu-3">
            {keyword ? 'Không tìm thấy tuyến/tủ nào khớp' : 'Chưa có tuyến đường nào'}
          </p>
        )}
        {filtered.map((r) => (
          <RouteNode key={r.id} route={r} forceOpen={keyword.trim().length > 0} />
        ))}
      </div>
    </div>
  );
}

function filterTree(routes: TreeRoute[], keyword: string): TreeRoute[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return routes;
  return routes
    .map((r) => {
      // Ô tìm khớp trên CẢ tên LẪN địa chỉ — gõ "Thủ Đức" phải ra được.
      const routeHit = `${r.tenDayDu} ${r.code ?? ''}`.toLowerCase().includes(kw);
      const cabinets = routeHit
        ? r.cabinets
        : r.cabinets.filter((c) => `${c.name} ${c.code} ${c.hardwareId}`.toLowerCase().includes(kw));
      return { ...r, cabinets };
    })
    .filter((r) => r.cabinets.length > 0);
}

function RouteNode({ route, forceOpen }: { route: TreeRoute; forceOpen: boolean }) {
  const params = useParams();
  const containsCurrent = route.cabinets.some((c) => c.id === params.id || c.id === params.cabinetId);
  const [open, setOpen] = useState(containsCurrent);

  useEffect(() => {
    if (containsCurrent || forceOpen) setOpen(true);
  }, [containsCurrent, forceOpen]);

  /*
   * ⛔ LUẬT YC-35 — CHỈ CỘNG NHỮNG TỦ ĐANG NỐI. `isOn` đến từ `CabinetState.outputs[].on`, và
   *   kênh ấy KHÔNG có chu kỳ: tủ mất kết nối thì cột ấy đông lạnh ở lần liên lạc cuối. Đang
   *   nối ⇒ số là hiện tại dù bản tin cũ bao nhiêu; mất nối ⇒ không biết, đừng cộng.
   */
  const tuDemDuoc = route.cabinets.filter((c) => c.status === 'ONLINE');
  const onCount = tuDemDuoc.reduce((n, c) => n + c.outputs.filter((o) => o.isOn).length, 0);
  const soKhongDem = route.cabinets.length - tuDemDuoc.length;

  return (
    <div className="mb-0.5">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-[40px] w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left hover:bg-nen-2 lg:min-h-[34px] lg:py-1"
      >
        {open ? <ChevronDown className="h-4 w-4 shrink-0 text-chu-3" /> : <ChevronRight className="h-4 w-4 shrink-0 text-chu-3" />}
        <span className="min-w-0 flex-1">
          {/* 🔴 tenDayDu, KHÔNG phải route.name — sidebar toàn "Nguyễn Huệ" trơ trọi là
              sidebar không chọn được (chốt YC-21). */}
          <span className="block truncate text-sm font-medium text-chu-1">{route.tenDayDu}</span>
          <span className="block truncate text-xs text-chu-2">
            {route.onlineCount}/{route.cabinetCount} tủ trực tuyến · {onCount} ngõ ra bật
            {soKhongDem > 0 ? ` (tủ tự khai · ${soKhongDem} tủ không nói thay được)` : ' (tủ tự khai)'}
          </span>
        </span>
      </button>

      {open && (
        <div className="ml-3 border-l border-vien pl-2">
          {route.cabinets.length === 0 && (
            <p className="px-2 py-1.5 text-xs text-chu-3">Tuyến chưa có tủ nào</p>
          )}
          {route.cabinets.map((c) => <CabinetNode key={c.id} cabinet={c} />)}
        </div>
      )}
    </div>
  );
}

function CabinetNode({ cabinet }: { cabinet: TreeCabinet }) {
  const online = cabinet.status === 'ONLINE';
  const on = cabinet.outputs.filter((o) => o.isOn).length;

  return (
    <NavLink
      to={`/tu/${cabinet.id}`}
      className={({ isActive }) =>
        [
          'flex min-h-[40px] items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition lg:min-h-[34px] lg:py-1',
          isActive ? 'bg-nhan/15 text-nhan' : 'text-chu-2 hover:bg-nen-2',
        ].join(' ')
      }
    >
      <StatusDot online={online} size="sm" />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{cabinet.name}</span>
        <span className="block truncate text-xs text-chu-3">{cabinet.code}</span>
      </span>
      {/*
        * ⛔ MẤT NỐI ⇒ KHÔNG VẼ SỐ. Huy hiệu "0/3" trên một tủ mất kết nối là một lời khai:
        *   nó nói "ba ngõ đang tắt", trong khi sự thật là **không ai biết**. `—` nói đúng.
        *   Nối lại thì tủ TỰ ĐẨY `CabinetState` ngay (nhánh `lan_dau`), nên khoảng không biết
        *   này không kéo dài — đó là thứ làm cửa "đang nối" đóng kín được.
        */}
      <span
        className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold ${
          !online ? 'bg-nen-2 text-chu-3' : on > 0 ? 'bg-tot/15 text-tot' : 'bg-nen-2 text-chu-2'
        }`}
        title={online
          ? `${on}/${cabinet.outputs.length} ngõ ra bật — TỦ TỰ KHAI, không phải số đọc từ tiếp điểm`
          : 'Tủ đang mất kết nối — không biết ngõ ra đang thế nào'}
      >
        {online ? `${on}/${cabinet.outputs.length}` : '—'}
      </span>
    </NavLink>
  );
}

/** Dùng ở màn hình cần nhắc người dùng rằng vai trò hiện tại chỉ được xem. */
export function ReadOnlyNotice({ need }: { need: Role }) {
  const { user } = useAuth();
  return (
    <div className="flex items-center gap-2 rounded-lg border border-vien bg-nen-2 px-3 py-2 text-xs text-chu-2">
      <ShieldCheck className="h-4 w-4 shrink-0 text-chu-3" />
      Vai trò <b className="mx-1">{user ? ROLE_LABEL[user.role] : ''}</b> chỉ được xem ở khu vực này —
      cần quyền <b className="mx-1">{ROLE_LABEL[need]}</b> trở lên để thao tác.
    </div>
  );
}
